import { useCallback, useId, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { IcnClose } from '../../icons/index.js';
import { IconButton } from '../IconButton/index.js';
import { useI18nT } from '../../i18n/index.js';
import { ensurePortalRoot } from '../shared/index.js';
import { useFocusTrap } from '../../hooks/useFocusTrap.js';
import { useRestoreFocus } from '../../hooks/useRestoreFocus.js';
import { useEscapeStack } from '../../hooks/useEscapeStack.js';
import { useBodyScrollLock } from '../../hooks/useBodyScrollLock.js';

export type DialogSize = 'sm' | 'md' | 'lg';
export type DialogTone = 'neutral' | 'info' | 'success' | 'warning' | 'danger';

export interface DialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title?: ReactNode;
  subtitle?: ReactNode;
  children?: ReactNode;
  /** Right-aligned footer slot. */
  footer?: ReactNode;
  /** When set, footer gets a sunken bg + top border (elevation separator).
   *  Default true. Set false for minimalist dialogs. */
  footerBar?: boolean;
  size?: DialogSize;
  /** Tone influences the optional tone-icon badge color. Default 'neutral'. */
  tone?: DialogTone;
  /** Icon slot shown in a tonal circle to the left of the title. */
  toneIcon?: ReactNode;
  /** Render an × close button in the header. Default true. */
  showClose?: boolean;
  /** Dismiss on backdrop click. Default true. */
  closeOnBackdrop?: boolean;
  /** Dismiss on Escape. Default true. */
  closeOnEscape?: boolean;
  /** Where to send focus on open. `first` → first focusable body element;
   *  `dialog` → the dialog container itself (useful for intro copy).
   *  Default `first`. */
  initialFocus?: 'first' | 'dialog';
  className?: string;
  'aria-label'?: string;
}

/** Modal dialog. Renders to portal, traps focus inside until closed, restores
 *  focus to whatever had it before opening. Backdrop + Escape are opt-out.
 *
 *  Focus bookkeeping delegated to the shared `useFocusTrap` + `useRestoreFocus`
 *  hooks — same primitives any other modal (Drawer, SettingsHub) uses. */
export function Dialog(props: DialogProps): ReactNode {
  const t = useI18nT();
  const {
    open,
    onOpenChange,
    title,
    subtitle,
    children,
    footer,
    footerBar = true,
    size = 'md',
    tone = 'neutral',
    toneIcon,
    showClose = true,
    closeOnBackdrop = true,
    closeOnEscape = true,
    initialFocus = 'first',
    className,
    'aria-label': ariaLabel,
  } = props;

  const backdropRef = useRef<HTMLDivElement | null>(null);
  const dialogRef = useRef<HTMLDivElement | null>(null);

  const reactId = useId();
  const titleId = `${reactId}-title`;
  const subtitleId = `${reactId}-subtitle`;

  const close = useCallback(() => {
    onOpenChange(false);
  }, [onOpenChange]);

  // Lock page scroll while open so background content can't be scrolled
  // behind the modal. The hook is reference-counted, so a Dialog inside
  // a Drawer doesn't double-lock; it also adds `paddingRight = scrollbar
  // width` so the page doesn't shift when the gutter collapses.
  useBodyScrollLock(open);

  useRestoreFocus(open);

  // The focus trap also handles initial focus placement. When `initialFocus
  // === 'dialog'` we point it at the dialog container; for `'first'` the hook
  // picks the first focusable descendant itself.
  useFocusTrap(dialogRef, {
    enabled: open,
    ...(initialFocus === 'dialog' ? { initialFocus: dialogRef } : {}),
  });

  // Escape via the shared modal stack — only the topmost layer responds
  // to a single Esc press. `closeOnEscape=false` opts out by passing
  // enabled=false (no stack registration), so the parent layer is free
  // to handle Esc.
  useEscapeStack(close, open && closeOnEscape);

  if (!open) return null;
  const portalRoot = ensurePortalRoot();
  if (portalRoot === null) return null;

  const dialogClasses = ['vq-dialog', className].filter(Boolean).join(' ');
  const hasHeader = title !== undefined || subtitle !== undefined || showClose;

  return createPortal(
    <div
      ref={backdropRef}
      className="vq-dialog-backdrop"
      data-component="DialogBackdrop"
      onMouseDown={(e) => {
        if (!closeOnBackdrop) return;
        if (e.target === backdropRef.current) close();
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title !== undefined ? titleId : undefined}
        aria-describedby={subtitle !== undefined ? subtitleId : undefined}
        aria-label={title === undefined ? ariaLabel : undefined}
        tabIndex={-1}
        className={dialogClasses}
        data-component="Dialog"
        data-size={size}
        data-tone={tone}
      >
        {hasHeader ? (
          <div
            className="vq-dialog__header"
            data-has-tone-icon={toneIcon !== undefined ? 'true' : undefined}
          >
            {toneIcon !== undefined ? (
              <span className="vq-dialog__tone-icon" aria-hidden="true">
                {toneIcon}
              </span>
            ) : null}
            <div className="vq-dialog__header-text">
              {title !== undefined ? (
                <h2 id={titleId} className="vq-dialog__title">
                  {title}
                </h2>
              ) : null}
              {subtitle !== undefined ? (
                <p id={subtitleId} className="vq-dialog__subtitle">
                  {subtitle}
                </p>
              ) : null}
            </div>
            {showClose ? (
              <IconButton
                className="vq-dialog__close"
                variant="ghost"
                size="sm"
                label={t('common.close')}
                onClick={close}
              >
                <IcnClose size={14} />
              </IconButton>
            ) : null}
          </div>
        ) : null}
        {children !== undefined ? (
          <div className="vq-dialog__body" data-pad={footer === undefined ? 'loose' : undefined}>
            {children}
          </div>
        ) : null}
        {footer !== undefined ? (
          <div className="vq-dialog__footer" data-plain={footerBar ? undefined : 'true'}>
            {footer}
          </div>
        ) : null}
      </div>
    </div>,
    portalRoot,
  );
}
